import { useEffect, useRef } from 'react';

/* ============================================================
   useSecurity — Maximum Practical Client-Side Protection

   IMPORTANT:
   Client-side protection is deterrence, NOT true IP protection.
   Anything delivered to a browser can ultimately be inspected.
   Keep sensitive logic/assets server-side.

   Protection layers:
   1. Context-menu blocking
   2. Keyboard shortcut deterrence
   3. Text-selection protection
   4. Copy / cut interception
   5. Drag protection
   6. Print protection
   7. DevTools heuristics
   8. Console warning
   9. Iframe / clickjacking guard
   10. Optional visibility protection
   11. Defensive cleanup
   ============================================================ */

const CONSOLE_WARNING = `
%c⚠ STOP — AUTHORIZED PERSONNEL ONLY
%cThis is a private portfolio. Unauthorized inspection,
scraping, or reproduction of code is prohibited.
© Madhu Vanguri — All Rights Reserved.
`;

const CONSOLE_STYLES = [
  'color:#ff4444;font-size:22px;font-weight:900;text-shadow:0 0 8px #ff4444;',
  'color:#ff9900;font-size:13px;font-weight:600;line-height:1.8;',
];

const CLIPBOARD_POISON =
  '\n\n[Content protected — © Madhu Vanguri. Unauthorized use is prohibited.]\n';

const DEVTOOLS_SIZE_THRESHOLD = 160;
const DEVTOOLS_TIMING_THRESHOLD = 100;

/* ------------------------------------------------------------
   DevTools detection — window size heuristic
   ------------------------------------------------------------ */
const isDevToolsOpenBySize = () => {
  if (typeof window === 'undefined') return false;

  return (
    window.outerWidth - window.innerWidth > DEVTOOLS_SIZE_THRESHOLD ||
    window.outerHeight - window.innerHeight > DEVTOOLS_SIZE_THRESHOLD
  );
};

/* ------------------------------------------------------------
   DevTools detection — debugger timing heuristic

   NOTE:
   This is only a deterrent. Browsers can bypass or alter this.
   ------------------------------------------------------------ */
const isDevToolsOpenByTiming = () => {
  if (typeof performance === 'undefined') return false;

  const start = performance.now();

  // eslint-disable-next-line no-debugger
  debugger;

  return performance.now() - start > DEVTOOLS_TIMING_THRESHOLD;
};

/* ============================================================
   HOOK
   ============================================================ */

const useSecurity = ({
  enableSelectionBlock = true,
  enableClipboardPoison = true,
  enableDevToolsDetection = true,
  enablePrintBlock = true,
  enableConsoleWarning = true,
  enableIframeGuard = true,

  /* Additional controls */
  enableContextMenuBlock = true,
  enableKeyboardBlock = true,
  enableDragBlock = true,
  enableVisibilityProtection = false,

  /*
    Recommended:
    Pass a React ref if you only want to protect a specific
    section of the website.

    Example:
      const protectedRef = useRef(null);

      useSecurity({
        protectedRootRef: protectedRef,
      });

      <main ref={protectedRef}>
        ...
      </main>
  */
  protectedRootRef = null,

  /* Optional callbacks */
  onDevToolsOpen = null,
  onVisibilityChange = null,
} = {}) => {
  const devToolsRef = useRef(false);
  const detectionIntervalRef = useRef(null);

  const devToolsCallbackRef = useRef(onDevToolsOpen);
  const visibilityCallbackRef = useRef(onVisibilityChange);

  /* Keep callbacks current without restarting the security effect */
  useEffect(() => {
    devToolsCallbackRef.current = onDevToolsOpen;
  }, [onDevToolsOpen]);

  useEffect(() => {
    visibilityCallbackRef.current = onVisibilityChange;
  }, [onVisibilityChange]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return undefined;
    }

    /*
      IMPORTANT ESLint fix:
      Capture the ref value once.

      React may change protectedRootRef.current before cleanup.
      Using this captured value prevents:
      react-hooks/exhaustive-deps
    */
    const protectedRoot = protectedRootRef?.current;

    /* ========================================================
       1. CONSOLE WARNING
       ======================================================== */

    /*
      We intentionally DO NOT overwrite console methods.

      Overwriting console.log/warn/etc can break:
      - React
      - third-party libraries
      - browser debugging
      - error diagnostics

      A warning is safer than destroying console functionality.
    */
    if (enableConsoleWarning) {
      console.log(CONSOLE_WARNING, ...CONSOLE_STYLES);
    }

    /* ========================================================
       2. IFRAME / CLICKJACKING GUARD
       ======================================================== */

    if (enableIframeGuard && window.self !== window.top) {
      try {
        window.top.location = window.self.location;
      } catch {
        /*
          Cross-origin iframe:
          JavaScript cannot safely access the parent location.

          Hide the page instead of throwing.
        */
        if (document.body) {
          document.body.style.visibility = 'hidden';
        }
      }
    }

    /* ========================================================
       3. CONTEXT MENU
       ======================================================== */

    const handleContextMenu = (event) => {
      if (!enableContextMenuBlock) return;

      event.preventDefault();
      event.stopPropagation();
    };

    /* ========================================================
       4. KEYBOARD SHORTCUT DETERRENCE
       ======================================================== */

    const handleKeyDown = (event) => {
      if (!enableKeyboardBlock) return;

      const modifier = event.ctrlKey || event.metaKey;
      const shift = event.shiftKey;

      const key = String(event.key || '').toLowerCase();

      /*
        F12
        Ctrl/Cmd + Shift + I
        Ctrl/Cmd + Shift + J
        Ctrl/Cmd + Shift + C
        Ctrl/Cmd + Shift + K
        Ctrl/Cmd + Shift + E
        Ctrl/Cmd + U
        Ctrl/Cmd + S
        Ctrl/Cmd + P
        Ctrl/Cmd + Shift + P
      */

      const blocked =
        key === 'f12' ||
        (modifier &&
          shift &&
          ['i', 'j', 'c', 'k', 'e'].includes(key)) ||
        (modifier && ['u', 's', 'p'].includes(key)) ||
        (modifier && shift && key === 'p');

      if (blocked) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    /* ========================================================
       5. TEXT SELECTION PROTECTION
       ======================================================== */

    let selectionStyle = null;

    if (enableSelectionBlock) {
      selectionStyle = document.createElement('style');
      selectionStyle.id = '__sec_sel__';

      /*
        If a protected ref was supplied, scope protection only
        to that area.

        This is much better for accessibility than globally
        disabling text selection across the entire website.
      */
      if (protectedRoot) {
        protectedRoot.dataset.securitySelectionProtected = 'true';

        selectionStyle.textContent = `
          [data-security-selection-protected],
          [data-security-selection-protected] *,
          [data-security-selection-protected] *::before,
          [data-security-selection-protected] *::after {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
          }

          [data-security-selection-protected] input,
          [data-security-selection-protected] textarea,
          [data-security-selection-protected] [contenteditable="true"] {
            -webkit-user-select: text !important;
            user-select: text !important;
          }
        `;
      } else {
        /*
          Backward-compatible fallback.

          If protectedRootRef is not supplied, this protects the
          entire page as the original hook did.
        */
        selectionStyle.textContent = `
          body[data-security-global-selection-block] * {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
          }

          body[data-security-global-selection-block] input,
          body[data-security-global-selection-block] textarea,
          body[data-security-global-selection-block] [contenteditable="true"] {
            -webkit-user-select: text !important;
            user-select: text !important;
          }
        `;

        document.body?.setAttribute(
          'data-security-global-selection-block',
          'true'
        );
      }

      document.head.appendChild(selectionStyle);
    }

    /* ========================================================
       6. COPY PROTECTION
       ======================================================== */

    const handleCopy = (event) => {
      if (!enableClipboardPoison) return;

      const active = document.activeElement;

      /*
        Never interfere with normal copying from:
        - input
        - textarea
        - contenteditable
      */
      const isEditable =
        active &&
        (
          active.matches?.(
            'input, textarea, [contenteditable="true"]'
          ) ||
          active.isContentEditable
        );

      if (isEditable) return;

      const selection =
        window.getSelection?.()?.toString() || '';

      if (!selection) return;

      try {
        event.clipboardData?.setData(
          'text/plain',
          selection + CLIPBOARD_POISON
        );

        event.preventDefault();
      } catch {
        /*
          Clipboard APIs may be restricted by the browser.
          Fail silently rather than breaking the page.
        */
      }
    };

    /* ========================================================
       7. CUT PROTECTION
       ======================================================== */

    const handleCut = (event) => {
      const active = document.activeElement;

      const isEditable =
        active &&
        (
          active.matches?.(
            'input, textarea, [contenteditable="true"]'
          ) ||
          active.isContentEditable
        );

      /*
        Allow normal cut operations inside editable controls.
      */
      if (isEditable) return;

      event.preventDefault();
    };

    /* ========================================================
       8. DRAG PROTECTION
       ======================================================== */

    const handleDragStart = (event) => {
      if (!enableDragBlock) return;

      const target = event.target;

      /*
        Don't interfere with editable elements.
      */
      if (
        target?.closest?.(
          'input, textarea, [contenteditable="true"]'
        )
      ) {
        return;
      }

      event.preventDefault();
    };

    /* ========================================================
       9. PRINT PROTECTION
       ======================================================== */

    const printClass = '__sec_print_block__';

    const handleBeforePrint = () => {
      if (!enablePrintBlock) return;

      document.documentElement.classList.add(printClass);
    };

    const handleAfterPrint = () => {
      document.documentElement.classList.remove(printClass);
    };

    const printStyle = document.createElement('style');

    printStyle.id = '__sec_print_style__';

    printStyle.textContent = `
      @media print {
        html.${printClass} body {
          visibility: hidden !important;
        }
      }
    `;

    if (enablePrintBlock) {
      document.head.appendChild(printStyle);
    }

    /* ========================================================
       10. VISIBILITY PROTECTION
       ======================================================== */

    const handleVisibilityChange = () => {
      if (!enableVisibilityProtection) return;

      const hidden =
        document.visibilityState === 'hidden';

      if (hidden) {
        document.documentElement.dataset.securityHidden =
          'true';
      } else {
        delete document.documentElement.dataset.securityHidden;
      }

      if (
        typeof visibilityCallbackRef.current === 'function'
      ) {
        visibilityCallbackRef.current(hidden);
      }
    };

    /* ========================================================
       11. DEVTOOLS DETECTION
       ======================================================== */

    const defaultDevToolsAction = () => {
      document.documentElement.dataset.securityDevtools =
        'true';

      document.body?.classList.add(
        '__security-devtools-open__'
      );
    };

    const restoreDevToolsAction = () => {
      delete document.documentElement.dataset.securityDevtools;

      document.body?.classList.remove(
        '__security-devtools-open__'
      );
    };

    if (enableDevToolsDetection) {
      detectionIntervalRef.current = window.setInterval(() => {
        /*
          Size detection is checked first because it is cheap.
        */
        let isOpen = isDevToolsOpenBySize();

        /*
          Timing detection is secondary because debugger timing
          can be noisy and browser-dependent.
        */
        if (!isOpen) {
          isOpen = isDevToolsOpenByTiming();
        }

        if (isOpen && !devToolsRef.current) {
          devToolsRef.current = true;

          if (
            typeof devToolsCallbackRef.current ===
            'function'
          ) {
            devToolsCallbackRef.current();
          } else {
            defaultDevToolsAction();
          }
        }

        if (!isOpen && devToolsRef.current) {
          devToolsRef.current = false;

          restoreDevToolsAction();
        }
      }, 2000);
    }

    /* ========================================================
       12. REGISTER EVENTS
       ======================================================== */

    if (enableContextMenuBlock) {
      document.addEventListener(
        'contextmenu',
        handleContextMenu,
        true
      );
    }

    if (enableKeyboardBlock) {
      document.addEventListener(
        'keydown',
        handleKeyDown,
        true
      );
    }

    document.addEventListener(
      'copy',
      handleCopy,
      true
    );

    document.addEventListener(
      'cut',
      handleCut,
      true
    );

    if (enableDragBlock) {
      document.addEventListener(
        'dragstart',
        handleDragStart,
        true
      );
    }

    if (enablePrintBlock) {
      window.addEventListener(
        'beforeprint',
        handleBeforePrint
      );

      window.addEventListener(
        'afterprint',
        handleAfterPrint
      );
    }

    if (enableVisibilityProtection) {
      document.addEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    }

    /* ========================================================
       13. CLEANUP
       ======================================================== */

    return () => {
      document.removeEventListener(
        'contextmenu',
        handleContextMenu,
        true
      );

      document.removeEventListener(
        'keydown',
        handleKeyDown,
        true
      );

      document.removeEventListener(
        'copy',
        handleCopy,
        true
      );

      document.removeEventListener(
        'cut',
        handleCut,
        true
      );

      document.removeEventListener(
        'dragstart',
        handleDragStart,
        true
      );

      window.removeEventListener(
        'beforeprint',
        handleBeforePrint
      );

      window.removeEventListener(
        'afterprint',
        handleAfterPrint
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );

      if (detectionIntervalRef.current !== null) {
        window.clearInterval(
          detectionIntervalRef.current
        );

        detectionIntervalRef.current = null;
      }

      devToolsRef.current = false;

      restoreDevToolsAction();

      document.documentElement.classList.remove(
        printClass
      );

      if (selectionStyle) {
        selectionStyle.remove();
      }

      /*
        Use the captured DOM node rather than
        protectedRootRef.current.

        This is the ESLint build-error fix.
      */
      if (protectedRoot) {
        delete protectedRoot.dataset
          .securitySelectionProtected;
      }

      document.body?.removeAttribute(
        'data-security-global-selection-block'
      );

      if (enablePrintBlock) {
        printStyle.remove();
      }
    };
  }, [
    enableSelectionBlock,
    enableClipboardPoison,
    enableDevToolsDetection,
    enablePrintBlock,
    enableConsoleWarning,
    enableIframeGuard,
    enableContextMenuBlock,
    enableKeyboardBlock,
    enableDragBlock,
    enableVisibilityProtection,
    protectedRootRef,
  ]);
};

export default useSecurity;

/* ============================================================
   USAGE EXAMPLES
   ============================================================ */

/*
   BASIC
   ------------------------------------------------------------

   import useSecurity from './hooks/useSecurity';

   const App = () => {
     useSecurity();

     return <div>...</div>;
   };
*/

/*
   RECOMMENDED — SCOPED PROTECTION
   ------------------------------------------------------------

   import { useRef } from 'react';
   import useSecurity from './hooks/useSecurity';

   const App = () => {
     const protectedRef = useRef(null);

     useSecurity({
       protectedRootRef: protectedRef,
       enableSelectionBlock: true,
       enableClipboardPoison: true,
       enableDevToolsDetection: true,
       enablePrintBlock: true,
       enableConsoleWarning: true,
       enableIframeGuard: true,
     });

     return (
       <main ref={protectedRef}>
         ...
       </main>
     );
   };
*/

/*
   DEVTOOLS CALLBACK
   ------------------------------------------------------------

   useSecurity({
     onDevToolsOpen: () => {
       console.warn('Developer tools detected');

       // Optional:
       // show warning
       // record analytics
       // redirect
       // blur content
     },
   });
*/

/*
   ACCESSIBILITY-FRIENDLY
   ------------------------------------------------------------

   useSecurity({
     enableSelectionBlock: false,
     enableClipboardPoison: false,
   });
*/

/*
   OPTIONAL VISIBILITY DETECTION
   ------------------------------------------------------------

   useSecurity({
     enableVisibilityProtection: true,

     onVisibilityChange: (hidden) => {
       if (hidden) {
         console.log('Page hidden');
       } else {
         console.log('Page visible');
       }
     },
   });
*/